/**
 * PATH: packages/email-hub/src/feature/lib/inlineImageFallback.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-INLINEIMAGEFALLBACK-001
 * PURPOSE: Pure helpers for the email-body image recovery path — classify
 *          a failed `<img>` src (so the hook knows which recovery strategy
 *          applies) and COLLAPSE a permanently-failed image to clean
 *          whitespace instead of the browser's native broken-image glyph
 *          OR a scary "couldn't load" error tile.
 * WHY:     REC-0002 — inline image placement keeps breaking email rendering
 *          (recurring operator complaint). Arc:
 *            1. raw broken-image box → looked like a render failure;
 *            2. (2026-06-21) a calm "Image couldn't be loaded — it may have
 *               expired or been removed" TILE for visibly-sized images;
 *            3. (2026-06-21) the operator hit a Microsoft
 *               OneDrive/SharePoint "shared a folder" notification whose
 *               thumbnail + branding images are auth-gated and can NEVER
 *               load in webmail (the same-origin proxy 403s too) → step 2
 *               painted TWO big error boxes, which read as the email
 *               "still glitching".
 *            4. (2026-06-21, this change) the boxes were gone but the
 *               broken-image glyph still FLASHED during the async recovery
 *               window (250 ms inline debounce + GET, or a 0.5–3 s proxy
 *               round-trip) before collapsing → "still glitches on load".
 *               Fix: hide the image the instant it errors
 *               (`hideWhileRecovering`) and only re-reveal it
 *               (`revealRecoveredImage`) if a recovery yields real bytes.
 *          A broken image carries ZERO information, and recovery (inline
 *          refetch / remote proxy) has already had its turn before we reach
 *          here — so the calm terminal state is to REMOVE the image from
 *          layout entirely, leaving the email's real text + actions (e.g.
 *          the "Open" link). Same outcome we already gave decorative icons;
 *          this just unifies every unrecoverable image onto it.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────┐   ┌────────────────────────┐
 *   │ failed <img> │ → │ classify (pure)   │ → │ proxy/refetch-recover   │
 *   │ src          │   │                   │   │ OR collapse (hide)      │
 *   └──────────────┘   └───────────────────┘   └────────────────────────┘
 * RELATED:
 *   - hooks/useInlineImageRecovery.ts (the imperative consumer)
 *   - lib/imageProxy.ts (remote recovery via same-origin backend proxy)
 *   - packages/ui/.../sanitizeHtml.ts (the "Image syncing…" placeholder
 *     for the pre-load case; this is its post-failure sibling)
 */

/** Querystring/path fragment present in every signed inline-image URL
 *  (`/api/email-attachments/<id>/inline/?u=&exp=&sig=`). Shared with the
 *  recovery hook so the "is this one of OUR images" test lives in one
 *  place. */
export const INLINE_URL_MARKER = '/inline/'

export type FailedImageKind =
  /** Our own signed inline-attachment URL — recoverable by refetching the
   *  email row (re-signs the URL) up to the hook's retry budget. */
  | 'inline'
  /** A third-party http(s) image — recoverable by routing through the
   *  same-origin backend image proxy (strips the CORS/referrer/hotlink
   *  constraints that block a direct browser load). */
  | 'remote'
  /** An analytics / open-tracking beacon. Never proxied: proxying would
   *  spend a backend round-trip, usually 502 on non-image HTML, and fire
   *  the sender's open tracker from our server IP (undoing sanitizer
   *  referrerpolicy=no-referrer). Collapses silently. */
  | 'beacon'
  /** Anything else (data:, blob:, relative, already-a-placeholder) — not
   *  network-recoverable, so it collapses straight away. */
  | 'local'

/**
 * True when a URL path/filename looks like an email open-tracking or
 * analytics beacon rather than a renderable body image.
 *
 * Positive-match only — do not treat non-image extensions (``.cfm``,
 * ``.php``) as beacons by themselves; legitimate CDNs serve images from
 * those. Path/filename tokens below are the durable signal.
 */
export function isLikelyTrackingBeaconUrl(src: string | null | undefined): boolean {
  if (!src) return false
  let path = ''
  try {
    path = new URL(src, 'https://example.invalid').pathname.toLowerCase()
  } catch {
    path = String(src).split('?')[0]?.toLowerCase() || ''
  }
  const file = path.split('/').pop() || ''
  if (
    /^(open|o|p|pixel|1x1|spacer|blank|transparent)(\.|$)/i.test(file) ||
    /\b(1x1|pixel|spacer|beacon)\b/i.test(file) ||
    /trackissueopening/i.test(file)
  ) {
    return true
  }
  // Path segments used by common ESP / journal open trackers.
  // Allow a following segment OR a file extension (``.cfm``, ``.gif``).
  return /(?:^|\/)(?:track(?:ing)?|beacon|pixel|wf\/open)(?:\/|\.|$)/i.test(path)
}

/** Declared width/height both at most 2px → tracking pixel / spacer. */
export function hasBeaconDeclaredSize(img: {
  getAttribute(name: string): string | null
}): boolean {
  const w = Number(img.getAttribute('width'))
  const h = Number(img.getAttribute('height'))
  if (!Number.isFinite(w) || !Number.isFinite(h)) return false
  return w > 0 && h > 0 && w <= 2 && h <= 2
}

/**
 * Classify a failed image's `src` so the recovery hook knows which
 * strategy applies. Pure: string → kind.
 */
export function classifyFailedImageSrc(src: string | null | undefined): FailedImageKind {
  if (!src) return 'local'
  if (src.includes(INLINE_URL_MARKER)) return 'inline'
  if (isLikelyTrackingBeaconUrl(src)) return 'beacon'
  if (/^https?:\/\//i.test(src)) return 'remote'
  return 'local'
}

/** Recovery state stamped on an `<img>` so the capture-phase error
 *  listener never reprocesses the same element (which would loop). */
export const IMG_RECOVERY_ATTR = 'data-img-recovery'
export type ImgRecoveryState = 'proxying' | 'proxied' | 'failed'

/**
 * Collapse an `<img>` the INSTANT it errors, BEFORE the (async) recovery
 * round-trip runs.
 *
 * WHY: the recovery strategies are asynchronous — an inline refetch waits
 * out a 250 ms debounce + a network GET, and a remote proxy fetch is a
 * full same-origin round-trip (often 0.5–3 s). Pre-fix the browser's
 * broken-image box stayed painted for that whole window, then collapsed
 * — the operator read the lingering-then-vanishing boxes as the email
 * "still glitching on load" (REC-0002). Hiding immediately means the
 * reader only ever sees clean whitespace while recovery happens behind
 * the scenes; `revealRecoveredImage` brings the element back iff (and only
 * iff) the recovery produced real bytes.
 *
 * Deliberately does NOT stamp `IMG_RECOVERY_ATTR='failed'` (that is the
 * TERMINAL state owned by {@link applyUnavailablePlaceholder}); the
 * caller stamps `'proxying'` for the remote path so the capture-phase
 * listener's idempotency guard still holds.
 */
export function hideWhileRecovering(img: HTMLImageElement): void {
  img.style.display = 'none'
}

/**
 * Undo {@link hideWhileRecovering} once a recovery produced displayable
 * bytes (e.g. the remote proxy returned an object URL). Restores the
 * element to the document flow so the recovered image paints.
 */
export function revealRecoveredImage(img: HTMLImageElement): void {
  img.style.display = ''
}

/** Marker attribute on the compact "Image unavailable — tap to retry"
 *  chip inserted next to a significant failed image. The recovery hook
 *  delegates clicks on `[data-img-retry-chip]` to {@link retryFailedImage}. */
export const IMG_RETRY_CHIP_ATTR = 'data-img-retry-chip'

/** Original failed src, stashed on the chip so a tap can re-run the
 *  whole recovery pipeline from the top. */
const RETRY_SRC_ATTR = 'data-img-retry-src'

/** Minimum declared/rendered dimension (px) for an image to earn a retry
 *  chip. Below this it's a tracking pixel or decorative icon — those keep
 *  the silent collapse (REC-0002: calm beats boxes). */
const SIGNIFICANT_IMAGE_MIN_PX = 64

/** Is this image big enough that its loss is CONTENT loss (worth a retry
 *  chip) rather than decoration? Uses declared attributes first (the
 *  layout intent), then any rendered/natural size we happen to know. */
export function isSignificantImage(img: HTMLImageElement): boolean {
  const declared = Math.max(
    Number(img.getAttribute('width')) || 0,
    Number(img.getAttribute('height')) || 0,
  )
  const rendered = Math.max(
    img.naturalWidth || 0, img.naturalHeight || 0,
    img.clientWidth || 0, img.clientHeight || 0,
  )
  return Math.max(declared, rendered) >= SIGNIFICANT_IMAGE_MIN_PX
}

/**
 * Collapse a permanently-failed `<img>` so the operator sees clean
 * whitespace instead of the native broken-image glyph or an error tile.
 *
 * The recovery path (refetch for `cid:` inline, proxy for remote) runs
 * FIRST in the hook; only genuinely dead images reach here. Tracking
 * pixels and decorative icons collapse silently (REC-0002). SIGNIFICANT
 * images (≥64px declared/rendered) additionally get a compact
 * "Image unavailable — tap to retry" chip (MR-C, audit 2026-07-06): the
 * silent collapse made the email look like content VANISHED — the user
 * couldn't tell an image was ever there, let alone retry it on
 * recovered wifi. The chip keeps the anti-broken-glyph intent while
 * making the failure legible and recoverable.
 *
 * Idempotent: marks the element `data-img-recovery="failed"` and no-ops on
 * re-entry. Returns true when it actually collapsed the element this call.
 */
export function applyUnavailablePlaceholder(img: HTMLImageElement): boolean {
  if (img.getAttribute(IMG_RECOVERY_ATTR) === 'failed') return false
  const wasSignificant = isSignificantImage(img)
  const failedSrc = img.currentSrc || img.src || ''
  // Mark first so a re-fired error on the same element is ignored.
  img.setAttribute(IMG_RECOVERY_ATTR, 'failed')
  // Drop `alt` so a broken image in a narrow table cell can't wrap its alt
  // text ONE CHARACTER PER LINE into a "glitching" vertical column
  // (BUG-2026-06-21-EMAIL-MS-CARD-ICON-ALT-VERTICAL-WRAP); drop `srcset`
  // so the browser doesn't retry a variant; drop the declared dimensions
  // so no empty reserved box lingers; hide so neither the glyph nor a dead
  // gap shows.
  img.removeAttribute('alt')
  img.removeAttribute('srcset')
  img.removeAttribute('width')
  img.removeAttribute('height')
  img.style.display = 'none'
  if (wasSignificant && failedSrc && img.parentNode) {
    insertRetryChip(img, failedSrc)
  }
  return true
}

/** Insert the compact retry chip right after the collapsed image.
 *  Inline-styled (the email body is sanitized third-party HTML — class
 *  names from our design system aren't guaranteed to survive its
 *  cascade). Idempotent per image. */
function insertRetryChip(img: HTMLImageElement, failedSrc: string): void {
  const next = img.nextElementSibling
  if (next && next.hasAttribute(IMG_RETRY_CHIP_ATTR)) return
  const doc = img.ownerDocument
  const chip = doc.createElement('button')
  chip.type = 'button'
  chip.setAttribute(IMG_RETRY_CHIP_ATTR, '')
  chip.setAttribute(RETRY_SRC_ATTR, failedSrc)
  chip.textContent = 'Image unavailable — tap to retry'
  chip.style.cssText = [
    'display:inline-flex', 'align-items:center', 'gap:4px',
    'margin:2px 0', 'padding:2px 10px', 'font-size:11px', 'line-height:1.6',
    'border:1px dashed rgba(128,128,128,0.5)', 'border-radius:9999px',
    'background:rgba(128,128,128,0.08)', 'color:inherit', 'opacity:0.75',
    'cursor:pointer', 'font-family:inherit',
  ].join(';')
  img.insertAdjacentElement('afterend', chip)
}

/**
 * Re-run the recovery pipeline for a chip's collapsed image (the chip
 * tap handler lives in useInlineImageRecovery, which also resets the
 * per-email retry budgets first so recovery genuinely gets another
 * turn). Removes the chip, clears the terminal state, restores the
 * original src — the reload either paints (wifi recovered) or errors
 * again and flows back through classify → refetch/proxy → chip.
 * Returns true when a retry was actually started.
 */
export function retryFailedImage(chip: Element): boolean {
  const src = chip.getAttribute(RETRY_SRC_ATTR)
  const img = chip.previousElementSibling
  chip.remove()
  if (!src || !(img instanceof HTMLImageElement)) return false
  img.removeAttribute(IMG_RECOVERY_ATTR)
  img.style.display = ''
  // Force a fresh load attempt even when the string is unchanged.
  img.removeAttribute('src')
  img.src = src
  return true
}
